import type { ReactElement } from 'react';
import { MARCAS_BY_ID, MARCA_VIEWBOX } from './marcas.generated';

export type MarcaProps = {
  /** Clave de la marca. Si no existe, no se pinta nada — nunca un icono roto. */
  id: string;
  size?: number;
  /**
   * `neutra` sobre superficie de lectura · `area` sobre un disco `--dim-*`.
   *
   * No es un detalle: los glifos se diseñaron en tinta clara sobre oscuro, y al
   * ponerlos oscuros sobre un disco de L 0.72 con el mismo grosor se cierran y
   * se ven sucios. El paso 7C midió la corrección — 1,4 en vez de 1,8 — y aquí
   * está aplicada.
   */
  polarity?: 'neutra' | 'area';
  className?: string;
  /** Texto accesible. `null` = decorativa (el nombre ya está al lado). */
  label?: string | null;
};

const STROKE = { neutra: 1.8, area: 1.4 } as const;

/**
 * Una de las 28 marcas de hábito. Pasaron dos rondas, una regla de gramática
 * —«la constante es el trazo, la retícula y el peso óptico, no la figura»— y la
 * prueba ciega a 20 px: 27 de 28 se leen sin etiqueta.
 */
export function Marca({
  id,
  size = 24,
  polarity = 'neutra',
  className,
  label = null,
}: MarcaProps): ReactElement | null {
  const marca = MARCAS_BY_ID.get(id);
  if (!marca) return null;

  const decorative = label === null;

  return (
    <svg
      viewBox={MARCA_VIEWBOX}
      width={size}
      height={size}
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE[polarity]}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...(decorative
        ? { 'aria-hidden': true, focusable: false }
        : { role: 'img', 'aria-label': label })}
    >
      <path d={marca.d} />
      {marca.f ? <path d={marca.f} fill="currentColor" stroke="none" /> : null}
    </svg>
  );
}
